import "./Notes.scss";
import "../../components/ModalContent/ModalComponents.scss";
import ActionBar from "../../components/ActionBar/ActionBar";
import ActionBarButton from "../../components/ActionBar/ActionBarButton";
import FlowTextInput from "../../components/FlowTextInput/FlowTextInput";
import FlowButton from "../../components/FlowButton/FlowButton";
import Modal from "../../components/Modal/Modal";
import EmptyStatePlaceholder from "../../components/EmptyStatePlaceholder/EmptyStatePlaceholder";
import RemoveNote from "./Modals/RemoveNote/RemoveNote";
import RemoveDeletedNotes from "./Modals/RemoveDeletedNotes/RemoveDeletedNotes";
import RearrangeNoteGroups from "./Modals/RearrangeNoteGroups/RearrangeNoteGroups";
import AddNoteGroup from "./Modals/AddNoteGroup/AddNoteGroup";
import EditNoteGroup from "./Modals/EditNoteGroup/EditNoteGroup";
import RemoveNoteGroup from "./Modals/RemoveNoteGroup/RemoveNoteGroup";
import TextColorPicker from "./Modals/TextColorPicker/TextColorPicker";
import EmojiPickerModal from "./Modals/EmojiPickerModal/EmojiPickerModal";
// Removed UnsavedChanges modal import
import { DEFAULT_BLUE } from "../../utils/colors";
import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { getAllNotes } from "../../api/notes/getAll";
import useAuthStore from "../../store/authStore";
import { fetcher } from "../../utils/fetcher";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { EmoticonReplacer } from "../../utils/emoticonExtension";
import { getTextPreview } from "../../utils/textPreview";
import { useProjectRealtime } from "../../hooks/useRealtime";
import OnboardingPointer from "../../components/OnboardingPointer/OnboardingPointer";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getLinkedCounts } from "../../api/tasks/getLinkedCounts";
import { jsPDF } from "jspdf";
import { loadExo2Font } from "../../utils/pdfFonts";

// Sortable Note Item Component
function SortableNoteItem({
  note,
  selectedNote,
  handleNoteSelect,
  handleDeleteNote,
  handleRestoreNote,
  linkedCount = 0,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: note._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`note-item ${selectedNote?._id === note._id ? "selected" : ""} ${!note.isActive ? "deleted" : ""}`}
      onClick={() => handleNoteSelect(note)}
    >
      <div className="note-info">
        <div className="note-title">
          {note.title}
          {linkedCount > 0 && (
            <span
              className="linked-indicator"
              title={`Linked to ${linkedCount} task${linkedCount > 1 ? "s" : ""}`}
            >
              <span className="material-symbols-outlined">link</span>
            </span>
          )}
        </div>
        <div className="note-meta">
          {getTextPreview(note.content || "", 120)}
        </div>
      </div>
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        {note.isActive && (
          <span
            className="material-symbols-outlined drag-handle"
            {...listeners}
            style={{
              fontSize: "20px",
              cursor: "grab",
              touchAction: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            drag_indicator
          </span>
        )}
        {!note.isActive ? (
          <span
            className="material-symbols-outlined restore-note-icon"
            onClick={(e) => handleRestoreNote(note._id, e)}
            style={{
              fontSize: "20px",
              cursor: "pointer",
            }}
          >
            restore_from_trash
          </span>
        ) : (
          <span
            className="material-symbols-outlined delete-note-icon"
            onClick={(e) => handleDeleteNote(note._id, e)}
            style={{
              fontSize: "20px",
              cursor: "pointer",
            }}
          >
            delete
          </span>
        )}
      </div>
    </div>
  );
}

// Empty Note Group Droppable Component
function EmptyNoteGroupDropZone({ noteGroupId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `empty-${noteGroupId}`,
  });

  return (
    <div
      ref={setNodeRef}
      className={`empty-note-dropzone ${isOver ? "over" : ""}`}
      style={{
        padding: "20px",
        textAlign: "center",
        color: "var(--secondary-text-color)",
        fontSize: "12px",
        opacity: isOver ? 1 : 0.5,
        backgroundColor: isOver ? "rgba(59, 130, 246, 0.15)" : "transparent",
        transition: "all 0.2s ease",
      }}
    >
      {/* {isOver ? "Drop here" : "Drag notes here"} */}
      {isOver ? "Drop here" : "Add Note by pressing + Add note"}
    </div>
  );
}

function Notes() {
  const [searchText, setSearchText] = useState("");
  const [noteGroups, setNoteGroups] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [activeNote, setActiveNote] = useState(null);
  const [linkedNoteCounts, setLinkedNoteCounts] = useState({});
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);
  const lastSavedNoteData = useRef({ title: "", content: "" });
  const editorScrollableRef = useRef(null);
  const fileInputRef = useRef(null);
  const hasRestoredNote = useRef(false);
  const isInitialMount = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const { user, currentProject, organizations, selectedNote, setSelectedNote } =
    useAuthStore();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // Require 10px movement before drag starts
      },
    }),
  );

  // Read search query and selectNote from URL on mount and when search params change
  useEffect(() => {
    const searchParam = searchParams.get("search");
    const selectNoteParam = searchParams.get("selectNote");

    if (searchParam) {
      setSearchText(searchParam);
    }

    // Clear the URL parameters after reading them
    if (searchParam || selectNoteParam) {
      setTimeout(() => {
        setSearchParams({});
      }, 200);
    }
  }, [searchParams, setSearchParams]);

  const handleImageUpload = async (file) => {
    if (!selectedNote || !editor) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Only image files are allowed");
      return;
    }

    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetcher.post(
        `/api/notes/${selectedNote._id}/upload-image`,
        formData,
      );

      if (response.success && response.image) {
        // Insert the thumbnail image into the editor with initial width
        editor
          .chain()
          .focus()
          .setImage({
            src: response.image.thumbnailUrl,
            alt: response.image.filename,
          })
          .run();
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      alert("Failed to upload image. Please try again.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      EmoticonReplacer,
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: "resizable-image",
        },
      }),
    ],
    content: "",
    editable: true,
    editorProps: {
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find((item) => item.type.startsWith("image/"));

        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file) {
            handleImageUpload(file);
          }
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          const target = event.target;
          if (target.tagName === "IMG") {
            const img = target;
            const rect = img.getBoundingClientRect();
            const cornerSize = 20;

            // Check if clicking in corners
            const isBottomRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isBottomLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isTopRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY < rect.top + cornerSize;

            const isTopLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY < rect.top + cornerSize;

            if (isBottomRight || isBottomLeft || isTopRight || isTopLeft) {
              event.preventDefault();

              const startX = event.clientX;
              const startY = event.clientY;
              const startWidth = img.offsetWidth;
              const aspectRatio = img.naturalHeight / img.naturalWidth;

              const handleMouseMove = (e) => {
                const diffX = e.clientX - startX;
                const diffY = e.clientY - startY;

                // Use the larger absolute difference to maintain aspect ratio
                const diff =
                  Math.abs(diffX) > Math.abs(diffY)
                    ? diffX
                    : diffY / aspectRatio;

                // Apply direction multiplier for left corners
                const directionMultiplier = isBottomLeft || isTopLeft ? -1 : 1;

                const newWidth = Math.max(
                  100,
                  Math.min(
                    startWidth + diff * directionMultiplier,
                    view.dom.offsetWidth - 40,
                  ),
                );

                img.style.width = newWidth + "px";
                img.style.height = "auto";
              };

              const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
                document.body.style.cursor = "";

                // Update the image node with new width
                const pos = view.posAtDOM(img, 0);
                if (pos !== null && pos !== undefined) {
                  const node = view.state.doc.nodeAt(pos);
                  if (node && node.type.name === "image") {
                    view.dispatch(
                      view.state.tr.setNodeMarkup(pos, null, {
                        ...node.attrs,
                        width: img.style.width,
                      }),
                    );
                  }
                }
              };

              // Set appropriate cursor
              if (isBottomRight || isTopLeft) {
                document.body.style.cursor = "nwse-resize";
              } else {
                document.body.style.cursor = "nesw-resize";
              }

              document.addEventListener("mousemove", handleMouseMove);
              document.addEventListener("mouseup", handleMouseUp);
            }
          }
          return false;
        },
        mousemove: (view, event) => {
          const target = event.target;
          if (target.tagName === "IMG") {
            const img = target;
            const rect = img.getBoundingClientRect();
            const cornerSize = 20;

            // Update cursor based on position
            const isBottomRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isBottomLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isTopRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY < rect.top + cornerSize;

            const isTopLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY < rect.top + cornerSize;

            if (isBottomRight || isTopLeft) {
              img.style.cursor = "nwse-resize";
            } else if (isBottomLeft || isTopRight) {
              img.style.cursor = "nesw-resize";
            } else {
              img.style.cursor = "default";
            }
          }
          return false;
        },
      },
    },
  });

  // Handle selectNote URL parameter - find and select the note
  // This must be after useEditor since it uses the editor
  useEffect(() => {
    const selectNoteParam = searchParams.get("selectNote");
    if (selectNoteParam && noteGroups.length > 0) {
      // Find the note across all groups
      for (const group of noteGroups) {
        const noteToSelect = group.notes?.find(
          (note) => note._id === selectNoteParam || note.id === selectNoteParam,
        );
        if (noteToSelect) {
          performNoteSelection(noteToSelect);
          break;
        }
      }
    }
  }, [searchParams, noteGroups, editor]);

  const fetchNotes = useCallback(() => {
    // Get current project directly from the store to avoid stale closures
    const currentProj = useAuthStore.getState().currentProject;

    // Don't fetch if there's no current project or project ID
    if (!currentProj || !currentProj._id) {
      setNoteGroups([]);
      setIsInitialLoadComplete(true);
      return;
    }

    getAllNotes(showCompleted, currentProj._id)
      .then((data) => {
        setNoteGroups(data);
        setIsInitialLoadComplete(true);
      })
      .catch((err) => {
        console.error("Error fetching note groups:", err);
        // If it's a projectId error, clear the note groups
        if (err.message?.includes("projectId")) {
          setNoteGroups([]);
        }
        setIsInitialLoadComplete(true);
      });
  }, [showCompleted]);

  const fetchLinkedCounts = useCallback(async () => {
    const currentProj = useAuthStore.getState().currentProject;
    if (!currentProj || !currentProj._id) {
      setLinkedNoteCounts({});
      return;
    }
    try {
      const data = await getLinkedCounts(currentProj._id);
      setLinkedNoteCounts(data.noteCounts || {});
    } catch (err) {
      console.error("Error fetching linked counts:", err);
    }
  }, []);

  useEffect(() => {
    // Fetch notes whenever currentProject changes (including when it loads from localStorage)
    if (currentProject?._id) {
      fetchNotes();
      fetchLinkedCounts();
    }
  }, [currentProject?._id, showCompleted, fetchNotes, fetchLinkedCounts]);

  // Real-time WebSocket updates for notes
  useProjectRealtime(currentProject?._id, {
    onNoteCreated: () => {
      fetchNotes();
    },
    onNoteUpdated: (updatedNote) => {
      // Don't refetch on note updates - the editor already has the latest data
      // and calling fetchNotes() during autosave causes unnecessary network traffic
      // Manual refresh or page reload will sync if needed
    },
    onNoteDeleted: () => {
      fetchNotes();
    },
    onNoteGroupCreated: () => {
      fetchNotes();
    },
    onNoteGroupUpdated: () => {
      fetchNotes();
    },
    onNoteGroupDeleted: () => {
      fetchNotes();
    },
  });

  // Restore selected note content when coming back to the page (only once on mount)
  useEffect(() => {
    if (
      selectedNote &&
      editor &&
      noteGroups.length > 0 &&
      !hasRestoredNote.current
    ) {
      // Find the note in the current noteGroups to ensure it still exists
      let foundNote = null;
      for (const group of noteGroups) {
        foundNote = group.notes?.find((n) => n._id === selectedNote._id);
        if (foundNote) break;
      }

      if (foundNote) {
        // Only restore if the editor is empty or different
        const currentContent = editor.getHTML();
        if (
          !currentContent ||
          currentContent === "<p></p>" ||
          currentContent !== foundNote.content
        ) {
          setNoteTitle(foundNote.title);
          editor.commands.setContent(foundNote.content || "");
          lastSavedNoteData.current = {
            title: foundNote.title.trim(),
            content: foundNote.content || "",
          };
        }
        hasRestoredNote.current = true;
      } else {
        // Note no longer exists, clear selection
        setSelectedNote(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteGroups, editor]);

  // Clear selected note when project changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setSelectedNote(null);
    hasRestoredNote.current = false;
  }, [currentProject?._id, setSelectedNote]);

  useEffect(() => {
    // Use setTimeout to ensure the component is fully rendered
    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Auto-update interval
  useEffect(() => {
    if (!autoUpdateEnabled || !selectedNote || !editor) return;

    const interval = setInterval(() => {
      const currentTitle = noteTitle.trim();
      const currentContent = editor.getHTML();

      // Check if there are changes
      if (
        currentTitle !== lastSavedNoteData.current.title ||
        currentContent !== lastSavedNoteData.current.content
      ) {
        // Auto-save in background (fire and forget)
        handleUpdateNote(true);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [autoUpdateEnabled, selectedNote, editor, noteTitle]);

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const collapseAll = () => {
    const newState = {};
    noteGroups.forEach((g) => {
      newState[g?.name] = true;
    });
    setCollapsedGroups(newState);
  };

  const expandAll = () => {
    const newState = {};
    noteGroups.forEach((g) => {
      newState[g?.name] = false;
    });
    setCollapsedGroups(newState);
  };

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const closeModal = () => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const getFilteredNoteGroups = () => {
    if (!searchText.trim()) {
      return noteGroups;
    }

    const searchLower = searchText.toLowerCase();

    return noteGroups
      .map((noteGroup) => ({
        ...noteGroup,
        notes: noteGroup.notes?.filter(
          (note) =>
            note.title.toLowerCase().includes(searchLower) ||
            note.content?.toLowerCase().includes(searchLower),
        ),
      }))
      .filter((noteGroup) => noteGroup.notes && noteGroup.notes.length > 0);
  };

  // Generate PDF for the selected note with images
  const generateNotePDF = async () => {
    if (!selectedNote) return;

    const doc = new jsPDF();

    // Load Exo 2 font
    await loadExo2Font(doc);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPosition = margin;

    // Helper to check if new content fits on page
    const checkPageBreak = (requiredHeight) => {
      if (yPosition + requiredHeight > pageHeight - 20) {
        doc.addPage();
        yPosition = margin;
      }
    };

    // Title
    doc.setFontSize(18);
    doc.setFont("Exo2", "bold");
    doc.text(selectedNote.title, margin, yPosition);
    yPosition += 8;

    // Subtitle with date
    doc.setFontSize(10);
    doc.setFont("Exo2", "normal");
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated ${new Date().toLocaleDateString()}`, margin, yPosition);
    yPosition += 15;

    // Reset text color
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont("Exo2", "normal");

    // Parse HTML content to extract text and images
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = selectedNote.content || "";

    // Process all child nodes (text and images)
    const processNode = async (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          const lines = doc.splitTextToSize(text, maxWidth);
          lines.forEach((line) => {
            checkPageBreak(6);
            doc.text(line, margin, yPosition);
            yPosition += 6;
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();

        if (tagName === "img") {
          const src = node.src || node.getAttribute("src");

          if (src) {
            try {
              let imageSrc = src;
              let format = "JPEG";

              // For external URLs, use proxy to avoid CORS issues
              if (!src.startsWith("data:")) {
                try {
                  // Get auth token
                  let token = null;
                  try {
                    const authStorage = localStorage.getItem("auth-storage");
                    if (authStorage) {
                      const parsedAuth = JSON.parse(authStorage);
                      token = parsedAuth.state?.token;
                    }
                  } catch (e) {
                    console.error("Error getting auth token:", e);
                  }

                  // Use full backend URL (same pattern as fetcher.js)
                  const baseURL =
                    import.meta.env.VITE_API_URL || "http://localhost:5001";
                  const proxyUrl = `${baseURL}/api/proxy-image?url=${encodeURIComponent(src)}`;
                  const response = await fetch(proxyUrl, {
                    headers: {
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                  });

                  if (!response.ok) {
                    throw new Error(`Proxy failed: ${response.status}`);
                  }

                  const blob = await response.blob();

                  // Detect format from blob type
                  if (blob.type === "image/png") {
                    format = "PNG";
                  } else if (
                    blob.type === "image/jpeg" ||
                    blob.type === "image/jpg"
                  ) {
                    format = "JPEG";
                  } else if (blob.type === "image/gif") {
                    format = "GIF";
                  }

                  // Convert blob to data URL
                  imageSrc = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                  });
                } catch (fetchError) {
                  throw fetchError;
                }
              } else {
                // Detect format from data URL
                if (src.startsWith("data:image/png")) {
                  format = "PNG";
                } else if (
                  src.startsWith("data:image/jpeg") ||
                  src.startsWith("data:image/jpg")
                ) {
                  format = "JPEG";
                } else if (src.startsWith("data:image/gif")) {
                  format = "GIF";
                }
              }

              // Calculate image dimensions
              const maxImgWidth = maxWidth;
              const maxImgHeight = 150; // Max height in PDF units

              // Load the image to get dimensions (use window.Image to avoid conflict with TipTap's Image import)
              const img = new window.Image();

              await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                  reject(new Error("Image load timeout"));
                }, 5000); // 5 second timeout

                img.onload = () => {
                  clearTimeout(timeout);
                  resolve();
                };
                img.onerror = (e) => {
                  clearTimeout(timeout);
                  reject(e);
                };
                img.src = imageSrc;
              });

              let imgWidth = img.width;
              let imgHeight = img.height;

              // Skip if image didn't load properly
              if (!imgWidth || !imgHeight) {
                throw new Error("Invalid image dimensions");
              }

              // Scale to fit within max dimensions while maintaining aspect ratio
              const widthScale = maxImgWidth / imgWidth;
              const heightScale = maxImgHeight / imgHeight;
              const scale = Math.min(widthScale, heightScale, 1); // Don't upscale

              imgWidth = imgWidth * scale;
              imgHeight = imgHeight * scale;

              // Check if image fits on current page
              checkPageBreak(imgHeight + 5);

              // Add image to PDF
              doc.addImage(
                imageSrc,
                format,
                margin,
                yPosition,
                imgWidth,
                imgHeight,
              );
              yPosition += imgHeight + 5; // Add spacing after image
            } catch (error) {
              console.error("Error adding image to PDF:", error);
              // Skip images that fail to load - don't show error message in PDF
            }
          }
        } else if (tagName === "p" || tagName === "div") {
          // Process paragraph/div content
          for (const child of node.childNodes) {
            await processNode(child);
          }
          yPosition += 3; // Paragraph spacing
        } else if (tagName === "br") {
          yPosition += 6;
        } else if (tagName === "li") {
          checkPageBreak(6);
          doc.text("• ", margin, yPosition);
          const text = node.textContent.trim();
          if (text) {
            const lines = doc.splitTextToSize(text, maxWidth - 5);
            lines.forEach((line, index) => {
              if (index > 0) {
                checkPageBreak(6);
                doc.text(line, margin + 5, yPosition);
              } else {
                doc.text(line, margin + 5, yPosition);
              }
              yPosition += 6;
            });
          }
        } else {
          // Process other elements recursively
          for (const child of node.childNodes) {
            await processNode(child);
          }
        }
      }
    };

    // Process all content
    for (const child of tempDiv.childNodes) {
      await processNode(child);
    }

    // Save the PDF
    const fileName = `${selectedNote.title.replace(/[^a-z0-9]/gi, "_")}.pdf`;
    doc.save(fileName);
  };

  const handleNoteSelect = (note) => {
    // If clicking the same note, do nothing
    if (selectedNote && note._id === selectedNote._id) {
      return;
    }
    performNoteSelection(note);
  };

  const performNoteSelection = (note) => {
    hasRestoredNote.current = true; // Mark as restored since we're manually selecting
    setSelectedNote(note);
    setNoteTitle(note.title);

    // Set baseline for auto-update comparison
    lastSavedNoteData.current = {
      title: note.title.trim(),
      content: note.content || "",
    };

    if (editor) {
      editor.commands.setContent(note.content || "");
      // Focus at the start of the content (top)
      editor.commands.focus("start");
    }

    // Scroll editor container to top
    if (editorScrollableRef.current) {
      editorScrollableRef.current.scrollTop = 0;
    }
  };

  const handleAddNote = async (noteGroupId, title) => {
    if (!title.trim()) return;

    try {
      const newNote = await fetcher.post("/api/notes", {
        groupId: noteGroupId,
        title: title.trim(),
        content: "",
        color: DEFAULT_BLUE, // Default color
        isActive: true,
      });

      // Refresh notes
      await fetchNotes();

      // Select the new note and focus on the editor
      setSelectedNote(newNote);
      setNoteTitle(title.trim());
      if (editor) {
        editor.commands.setContent("");
        editor.commands.focus();
      }
    } catch (err) {
      console.error("Error creating note:", err);
    }
  };

  const handleUpdateNote = useCallback(
    async (isBackground = false) => {
      if (!selectedNote || !editor) return;

      const updatedTitle = noteTitle.trim();
      const updatedContent = editor.getHTML();

      if (isBackground) {
        // Background save: fire and forget, no UI feedback, no waiting
        fetcher
          .put(`/api/notes/${selectedNote._id}`, {
            title: noteTitle,
            content: updatedContent,
          })
          .then(() => {
            // Update baseline after successful save
            lastSavedNoteData.current = {
              title: updatedTitle,
              content: updatedContent,
            };

            // Update the note in local noteGroups state to avoid stale data
            setNoteGroups((prev) =>
              prev.map((group) => ({
                ...group,
                notes: group.notes?.map((note) =>
                  note._id === selectedNote._id
                    ? { ...note, title: noteTitle, content: updatedContent }
                    : note,
                ),
              })),
            );
          })
          .catch((err) => {
            console.error("Error updating note (background):", err);
          });
        return;
      }

      // Manual save: show feedback and wait for response
      setIsUploading(true);

      // Ensure the spin animation runs for at least 1 second
      const minimumSpinTime = new Promise((resolve) =>
        setTimeout(resolve, 1000),
      );

      try {
        await fetcher.put(`/api/notes/${selectedNote._id}`, {
          title: noteTitle,
          content: updatedContent,
        });

        // Update baseline after successful save
        lastSavedNoteData.current = {
          title: updatedTitle,
          content: updatedContent,
        };

        // Update the note in local noteGroups state to avoid stale data
        setNoteGroups((prev) =>
          prev.map((group) => ({
            ...group,
            notes: group.notes?.map((note) =>
              note._id === selectedNote._id
                ? { ...note, title: noteTitle, content: updatedContent }
                : note,
            ),
          })),
        );

        // Don't call fetchNotes() here - it causes the editor to lose focus during typing
        // The WebSocket will handle real-time updates from other clients
        // Manual updates (button click) will refresh via separate flow if needed
      } catch (err) {
        console.error("Error updating note:", err);
      } finally {
        // Wait for the minimum spin time before stopping the animation
        await minimumSpinTime;
        setIsUploading(false);
      }
    },
    [selectedNote, editor, noteTitle],
  );

  const handleDeleteNote = (noteId, e) => {
    e.stopPropagation(); // Prevent note selection when clicking delete

    openModal(
      e,
      "center-center",
      <RemoveNote
        removeNote={() => confirmDeleteNote(noteId)}
        cancelRemoveNote={closeModal}
      />,
    );
  };

  const confirmDeleteNote = async (noteId) => {
    try {
      await fetcher.put(`/api/notes/${noteId}`, {
        isActive: false,
      });

      // If the deleted note was selected, clear the selection
      if (selectedNote?._id === noteId) {
        setSelectedNote(null);
        setNoteTitle("");
        if (editor) {
          editor.commands.setContent("");
        }
      }

      // Refresh notes
      await fetchNotes();
      closeModal();
    } catch (err) {
      console.error("Error deleting note:", err);
    }
  };

  const handleRestoreNote = async (noteId, e) => {
    e.stopPropagation(); // Prevent note selection when clicking restore

    try {
      await fetcher.put(`/api/notes/${noteId}`, {
        isActive: true,
      });

      // Refresh notes
      await fetchNotes();
    } catch (err) {
      console.error("Error restoring note:", err);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    const { active } = event;

    // Find the note being dragged
    for (const group of noteGroups) {
      const note = group.notes?.find((n) => n._id === active.id);
      if (note) {
        setActiveNote(note);
        break;
      }
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Check if dropping on an empty note group
    const isEmptyDropZone = over.id.toString().startsWith("empty-");

    // Find source note and group
    let sourceGroup = null;
    let sourceNoteIndex = -1;

    for (const group of noteGroups) {
      const noteIndex =
        group.notes?.findIndex((n) => n._id === active.id) ?? -1;
      if (noteIndex !== -1) {
        sourceGroup = group;
        sourceNoteIndex = noteIndex;
        break;
      }
    }

    if (!sourceGroup) {
      return;
    }

    // Handle dropping on empty note group
    if (isEmptyDropZone) {
      const targetGroupId = over.id.toString().replace("empty-", "");
      const targetGroup = noteGroups.find((g) => g._id === targetGroupId);

      if (!targetGroup || sourceGroup._id === targetGroup._id) {
        return;
      }

      const newNoteGroups = noteGroups.map((g) => ({
        ...g,
        notes: [...(g.notes || [])],
      }));

      const sourceGroupIndex = newNoteGroups.findIndex(
        (g) => g._id === sourceGroup._id,
      );
      const targetGroupIndex = newNoteGroups.findIndex(
        (g) => g._id === targetGroupId,
      );

      // Remove from source
      const [movedNote] = newNoteGroups[sourceGroupIndex].notes.splice(
        sourceNoteIndex,
        1,
      );

      // Add to empty target group
      newNoteGroups[targetGroupIndex].notes.push(movedNote);

      // Update UI immediately
      setNoteGroups(newNoteGroups);
      return;
    }

    // Find target note and group (existing logic)
    let targetGroup = null;
    let targetNoteIndex = -1;

    for (const group of noteGroups) {
      const noteIndex = group.notes?.findIndex((n) => n._id === over.id) ?? -1;
      if (noteIndex !== -1) {
        targetGroup = group;
        targetNoteIndex = noteIndex;
        break;
      }
    }

    if (!sourceGroup || !targetGroup) {
      return;
    }

    // Only update if moving between groups or different positions
    if (
      sourceGroup._id !== targetGroup._id ||
      sourceNoteIndex !== targetNoteIndex
    ) {
      const newNoteGroups = noteGroups.map((g) => ({
        ...g,
        notes: [...(g.notes || [])],
      }));

      const sourceGroupIndex = newNoteGroups.findIndex(
        (g) => g._id === sourceGroup._id,
      );
      const targetGroupIndex = newNoteGroups.findIndex(
        (g) => g._id === targetGroup._id,
      );

      // Remove from source
      const [movedNote] = newNoteGroups[sourceGroupIndex].notes.splice(
        sourceNoteIndex,
        1,
      );

      // Insert into target position
      newNoteGroups[targetGroupIndex].notes.splice(
        targetNoteIndex,
        0,
        movedNote,
      );

      // Update UI immediately
      setNoteGroups(newNoteGroups);
    }
  };

  const handleDragEnd = async (event) => {
    const { over } = event;

    // Clear the active note
    setActiveNote(null);

    if (!over) {
      return;
    }

    // The UI has already been updated by handleDragOver, so we need to use the CURRENT state
    // to generate the correct sort orders for the backend
    const updates = [];

    // Just use the current noteGroups state (which has already been updated by handleDragOver)
    // and generate sortOrder for all notes
    noteGroups.forEach((group) => {
      group.notes?.forEach((note, index) => {
        updates.push({
          _id: note._id,
          sortOrder: index,
          noteGroupId: group._id,
        });
      });
    });

    if (updates.length === 0) {
      return;
    }

    try {
      await fetcher.put("/api/notes/reorder", updates);
      // The backend will emit a socket event, but we've already updated the UI
    } catch (err) {
      console.error("Error reordering notes:", err);
      // If there's an error, refetch to get the correct state
      fetchNotes();
    }
  };

  const cancelRemoveDeletedNotes = () => {
    closeModal();
  };

  const removeDeletedNotes = async () => {
    try {
      await fetcher.delete(
        `/api/notes/completed?projectId=${currentProject?._id}`,
      );

      // Clear selection if a deleted note was selected
      if (selectedNote && !selectedNote.isActive) {
        setSelectedNote(null);
        setNoteTitle("");
        if (editor) {
          editor.commands.setContent("");
        }
      }

      // Toggle off "show deleted" since there are no more deleted notes to show
      setShowCompleted(false);

      await fetchNotes();
      closeModal();
    } catch (err) {
      console.error("Error removing deleted notes:", err);
    }
  };

  const closeRearrangeNoteGroups = () => {
    closeModal();
  };

  const handleAddNoteGroup = (e) => {
    openModal(
      e,
      "top-left",
      <AddNoteGroup
        createNoteGroup={createNoteGroup}
        cancelCreateNoteGroup={closeModal}
      />,
    );
  };

  const handleTextColorPicker = (e) => {
    openModal(
      e,
      "top-center",
      <TextColorPicker
        onSelectColor={(color) => {
          editor?.chain().focus().setColor(color).run();
          closeModal();
        }}
      />,
    );
  };

  const handleEmojiPicker = (e) => {
    openModal(
      e,
      "top-center",
      <EmojiPickerModal
        onSelectEmoji={(emoji) => {
          editor?.commands.insertContent(emoji);
          closeModal();
        }}
      />,
    );
  };

  const createNoteGroup = async (name, color) => {
    try {
      // Calculate the next sortOrder (highest current + 1)
      const maxSortOrder = noteGroups.reduce((max, group) => {
        return group.sortOrder > max ? group.sortOrder : max;
      }, 0);

      await fetcher.post("/api/noteGroups", {
        name,
        color,
        projectId: currentProject?._id,
        sortOrder: maxSortOrder + 1,
      });
      await fetchNotes();
      closeModal();
    } catch (err) {
      console.error("Error creating note group:", err);
    }
  };

  const handleEditNoteGroup = (e, noteGroup) => {
    e.stopPropagation();
    openModal(
      e,
      "top-left",
      <EditNoteGroup
        noteGroupData={noteGroup}
        updateNoteGroup={updateNoteGroup}
        cancelEditNoteGroup={closeModal}
      />,
    );
  };

  const updateNoteGroup = async (noteGroupId, name, color) => {
    try {
      await fetcher.put(`/api/noteGroups/${noteGroupId}`, {
        name,
        color,
      });
      await fetchNotes();
      closeModal();
    } catch (err) {
      console.error("Error updating note group:", err);
    }
  };

  const handleDeleteNoteGroup = (e, noteGroup) => {
    e.stopPropagation();

    openModal(
      e,
      "center-center",
      <RemoveNoteGroup
        removeNoteGroup={() => confirmDeleteNoteGroup(noteGroup)}
        cancelRemoveNoteGroup={closeModal}
        noteGroupName={noteGroup.name}
      />,
    );
  };

  const confirmDeleteNoteGroup = async (noteGroup) => {
    try {
      await fetcher.put(`/api/noteGroups/${noteGroup._id}`, {
        isActive: false,
      });
      // If the current selected note belongs to this group, clear selection
      if (
        selectedNote &&
        noteGroup.notes?.some((note) => note._id === selectedNote._id)
      ) {
        setSelectedNote(null);
        setNoteTitle("");
        if (editor) {
          editor.commands.setContent("");
        }
      }
      closeModal();
      await fetchNotes();
    } catch (err) {
      console.error("Error deleting note group:", err);
    }
  };

  // Check if user should see onboarding pointer for note groups
  const isNotFiltering = !searchText.trim();
  const hasNoNoteGroups =
    currentProject &&
    noteGroups.length === 0 &&
    isNotFiltering &&
    isInitialLoadComplete;
  const showNoteGroupOnboarding = hasNoNoteGroups;

  return (
    <div className="notes-wrapper">
      <OnboardingPointer
        targetSelector=".add-notegroup-button"
        message="Create notegroup"
        show={showNoteGroupOnboarding}
      />
      <ActionBar>
        {currentProject && (
          <>
            <ActionBarButton
              className="add-notegroup-button"
              buttonText="Add Notegroup"
              icon={
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px" }}
                >
                  assignment_add
                </span>
              }
              onClick={handleAddNoteGroup}
            />
            <div className="action-bar-spacer" />
          </>
        )}
        <ActionBarButton
          buttonText="Collapse All"
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              collapse_content
            </span>
          }
          onClick={collapseAll}
        />
        <div className="action-bar-spacer" />
        <ActionBarButton
          buttonText="Expand All"
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              expand_content
            </span>
          }
          onClick={expandAll}
        />
        <div className="action-bar-spacer" />
        <ActionBarButton
          buttonText="Rearrange Groups"
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              move_down
            </span>
          }
          onClick={(e) =>
            openModal(
              e,
              "center-center",
              <RearrangeNoteGroups
                noteGroups={noteGroups}
                closeRearrangeNoteGroups={closeRearrangeNoteGroups}
                onSave={fetchNotes}
              />,
            )
          }
        />
        <div className="action-bar-spacer" />
        {showCompleted ? (
          <ActionBarButton
            buttonText="Hide Deleted"
            icon={
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "20px" }}
              >
                visibility_off
              </span>
            }
            onClick={() => setShowCompleted(false)}
          />
        ) : (
          <ActionBarButton
            buttonText="Show Deleted"
            icon={
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "20px" }}
              >
                visibility
              </span>
            }
            onClick={() => setShowCompleted(true)}
          />
        )}
        {showCompleted ? (
          <>
            <div className="action-bar-spacer" />
            <ActionBarButton
              buttonText="Remove Deleted"
              textColor="#e8697d"
              icon={
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px", color: "#e8697d" }}
                >
                  delete
                </span>
              }
              onClick={(e) =>
                openModal(
                  e,
                  "top-center",
                  <RemoveDeletedNotes
                    cancelRemoveDeletedNotes={cancelRemoveDeletedNotes}
                    removeDeletedNotes={removeDeletedNotes}
                  />,
                )
              }
            />
          </>
        ) : (
          <></>
        )}
        <div className="action-bar-spacer" />
        <ActionBarButton
          buttonText="Download PDF"
          icon={
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "20px" }}
            >
              picture_as_pdf
            </span>
          }
          onClick={generateNotePDF}
          disabled={!selectedNote || !selectedNote.isActive}
        />
      </ActionBar>
      <div className="notes">
        <div className="notes-content">
          <div className="notes-left-column">
            <div className="notes-column-header">Notegroups</div>
            <div className="notes-search">
              <FlowTextInput
                ref={searchInputRef}
                placeholder="Filter notes..."
                value={searchText}
                onChange={handleSearchChange}
                expandable={false}
                icon="search"
              />
            </div>
            {getFilteredNoteGroups().length === 0 ? (
              <EmptyStatePlaceholder
                topText="No notes found"
                bottomText={
                  searchText.trim()
                    ? "Try adjusting your search"
                    : "Create a notegroup to get started"
                }
              />
            ) : (
              <>
                <DndContext
                  sensors={sensors}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                >
                  <div className="notes-list">
                    {getFilteredNoteGroups().map((noteGroup) => (
                      <div
                        key={noteGroup._id}
                        className="notegroup-wrapper"
                        style={{
                          "--notegroup-color": noteGroup.color,
                        }}
                      >
                        <div className="notegroup-header">
                          <div className="notegroup-title-container">
                            <span
                              className="material-symbols-outlined collapse-icon"
                              style={{ fontSize: "28px", cursor: "pointer" }}
                              onClick={() => toggleGroup(noteGroup.name)}
                            >
                              {collapsedGroups[noteGroup.name]
                                ? "keyboard_arrow_right"
                                : "keyboard_arrow_down"}
                            </span>
                            <span
                              className="notegroup-name"
                              onClick={() => toggleGroup(noteGroup.name)}
                              style={{ cursor: "pointer" }}
                            >
                              {noteGroup.name}
                            </span>
                            {collapsedGroups[noteGroup.name] && (
                              <span className="notegroup-count">
                                ({noteGroup.notes?.length || 0}{" "}
                                {noteGroup.notes?.length === 1
                                  ? "note"
                                  : "notes"}
                                )
                              </span>
                            )}
                            <span
                              className="material-symbols-outlined more-horiz"
                              style={{
                                fontSize: "24px",
                                marginLeft: "10px",
                                cursor: "pointer",
                              }}
                              onClick={(e) => handleEditNoteGroup(e, noteGroup)}
                            >
                              more_horiz
                            </span>
                            <span
                              className="material-symbols-outlined delete-icon"
                              style={{
                                fontSize: "24px",
                                marginLeft: "auto",
                                cursor: "pointer",
                                color: "white",
                              }}
                              onClick={(e) =>
                                handleDeleteNoteGroup(e, noteGroup)
                              }
                            >
                              delete
                            </span>
                          </div>
                        </div>
                        {!collapsedGroups[noteGroup.name] && (
                          <SortableContext
                            items={(noteGroup.notes || []).map(
                              (note) => note._id,
                            )}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="notegroup-container">
                              {noteGroup.notes &&
                              noteGroup.notes.length === 0 ? (
                                <EmptyNoteGroupDropZone
                                  noteGroupId={noteGroup._id}
                                />
                              ) : (
                                noteGroup.notes?.map((note) => (
                                  <SortableNoteItem
                                    key={note._id}
                                    note={note}
                                    selectedNote={selectedNote}
                                    handleNoteSelect={handleNoteSelect}
                                    handleDeleteNote={handleDeleteNote}
                                    handleRestoreNote={handleRestoreNote}
                                    linkedCount={
                                      linkedNoteCounts[note._id] || 0
                                    }
                                  />
                                ))
                              )}
                              <div className="add-note">
                                <input
                                  type="text"
                                  className="add-note-input"
                                  placeholder="+ Add note"
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleAddNote(
                                        noteGroup.id,
                                        e.target.value,
                                      );
                                      e.target.value = "";
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </SortableContext>
                        )}
                      </div>
                    ))}
                  </div>
                  <DragOverlay>
                    {activeNote ? (
                      <div
                        className={`note-item ${!activeNote.isActive ? "deleted" : ""}`}
                        style={{
                          cursor: "grabbing",
                          opacity: 0.9,
                        }}
                      >
                        <div className="note-info">
                          <div className="note-title">{activeNote.title}</div>
                          <div className="note-meta">
                            {getTextPreview(activeNote.content || "", 50)}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              </>
            )}
          </div>
          <div className="note-editor-column">
            <div className="notes-column-header">Note</div>
            {!selectedNote ? (
              <EmptyStatePlaceholder
                topText="Please select a note"
                bottomText="... if you have one"
              />
            ) : (
              <>
                <div className="note-title-bar">
                  <FlowTextInput
                    className="note-title-flow-input"
                    placeholder="Note title..."
                    value={noteTitle}
                    onChange={(value) => setNoteTitle(value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleUpdateNote();
                      }
                    }}
                    expandable={false}
                    icon=""
                  />
                  <FlowButton variant="update" onClick={handleUpdateNote}>
                    <span style={{ position: "relative", top: "1px" }}>
                      Save
                    </span>
                    {autoUpdateEnabled && (
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: "16px",
                          marginLeft: "4px",
                          verticalAlign: "middle",
                          animation: isUploading
                            ? "spin 1s ease-in-out"
                            : "none",
                        }}
                      >
                        cached
                      </span>
                    )}
                  </FlowButton>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5em",
                      marginLeft: "1em",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={autoUpdateEnabled}
                      onChange={(e) => setAutoUpdateEnabled(e.target.checked)}
                    />
                    Auto-save (every 5 seconds)
                  </label>
                </div>
                <style>
                  {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
                </style>
                <div className="note-editor-wrapper">
                  <div className="note-editor-controls">
                    <button
                      className="editor-btn"
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                    >
                      <span className="material-symbols-outlined">
                        format_bold
                      </span>
                    </button>
                    <button
                      className="editor-btn"
                      onClick={() =>
                        editor?.chain().focus().toggleItalic().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_italic
                      </span>
                    </button>
                    <button
                      className="editor-btn"
                      onClick={() =>
                        editor?.chain().focus().toggleStrike().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_strikethrough
                      </span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className="editor-btn"
                      onClick={() =>
                        editor
                          ?.chain()
                          .focus()
                          .toggleHeading({ level: 1 })
                          .run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_h1
                      </span>
                    </button>
                    <button
                      className="editor-btn"
                      onClick={() =>
                        editor
                          ?.chain()
                          .focus()
                          .toggleHeading({ level: 2 })
                          .run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_h2
                      </span>
                    </button>
                    <button
                      className="editor-btn"
                      onClick={() =>
                        editor
                          ?.chain()
                          .focus()
                          .toggleHeading({ level: 3 })
                          .run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_h3
                      </span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className="editor-btn"
                      onClick={handleTextColorPicker}
                      title="Text color"
                    >
                      <span className="material-symbols-outlined">
                        format_color_text
                      </span>
                    </button>
                    <button
                      className="editor-btn"
                      onClick={handleEmojiPicker}
                      title="Insert emoji"
                    >
                      <span className="material-symbols-outlined">mood</span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className="editor-btn"
                      onClick={() =>
                        editor?.chain().focus().toggleBulletList().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_list_bulleted
                      </span>
                    </button>
                    <button
                      className="editor-btn"
                      onClick={() =>
                        editor?.chain().focus().toggleOrderedList().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_list_numbered
                      </span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className="editor-btn"
                      onClick={() =>
                        editor?.chain().focus().toggleBlockquote().run()
                      }
                    >
                      <span className="material-symbols-outlined">
                        format_quote
                      </span>
                    </button>
                    <button
                      className="editor-btn"
                      onClick={() => {
                        if (!editor) return;

                        const { from, to } = editor.state.selection;
                        const selectedText = editor.state.doc.textBetween(
                          from,
                          to,
                          "\n",
                        );

                        // If we have selected text across multiple paragraphs
                        if (selectedText && selectedText.includes("\n")) {
                          editor
                            .chain()
                            .focus()
                            .deleteSelection()
                            .insertContent({
                              type: "codeBlock",
                              content: [{ type: "text", text: selectedText }],
                            })
                            .run();
                        } else {
                          // Normal toggle for single line or empty
                          editor.chain().focus().toggleCodeBlock().run();
                        }
                      }}
                    >
                      <span className="material-symbols-outlined">code</span>
                    </button>
                    <span className="editor-separator" />
                    <button
                      className="editor-btn"
                      onClick={handleImageButtonClick}
                      disabled={isUploadingImage}
                      title="Upload image"
                    >
                      <span className="material-symbols-outlined">
                        {isUploadingImage ? "hourglass_empty" : "image"}
                      </span>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />
                  </div>

                  <div
                    ref={editorScrollableRef}
                    className="note-editor-scrollable"
                    onClick={() => {
                      if (editor && !editor.isFocused) {
                        // Only focus at end if editor is not already focused
                        editor.commands.focus("end");
                      }
                    }}
                  >
                    <EditorContent
                      editor={editor}
                      className="note-content-area"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Modal ref={modalRef} />
    </div>
  );
}

export default Notes;
